import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import translations from '../locales/translations.json';

const STORAGE_KEY = 'astrum_language';
const PROFILE_LANGUAGE_KEY = 'astrum_profile_language_loaded';

// Supported languages - SIMPLIFIED to EN/ES only for stability
export const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', label: 'EN' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', label: 'ES' }
];

// Detect browser language - defaults to Spanish if not English
const detectBrowserLanguage = () => {
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const langCode = browserLang.split('-')[0].toLowerCase();
    
    // Check if we support this language
    const supported = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
    // FAILSAFE: Default to Spanish for non-English users (Latin American market)
    return supported ? langCode : (langCode === 'en' ? 'en' : 'es');
};

// Get initial language from localStorage or browser
const getInitialLanguage = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.find(l => l.code === stored)) {
        return stored;
    }
    return detectBrowserLanguage();
};

// Context
const LanguageContext = createContext(null);

// Provider component with proper loading state handling
export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState(getInitialLanguage);
    const [isRTL, setIsRTL] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const languageResolvedRef = useRef(false);

    // Listen for storage events (when AuthGatekeeper updates language)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY && e.newValue) {
                const newLang = e.newValue;
                if (SUPPORTED_LANGUAGES.find(l => l.code === newLang) && newLang !== language) {
                    console.log(`[Language] Storage event: updating to ${newLang}`);
                    setLanguageState(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [language]);

    // Watch for profile language being set by AuthGatekeeper
    useEffect(() => {
        if (languageResolvedRef.current) return;

        // Function to check and sync language
        const syncLanguage = () => {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored && SUPPORTED_LANGUAGES.find(l => l.code === stored)) {
                if (stored !== language) {
                    console.log(`[Language] Syncing from localStorage: ${stored}`);
                    setLanguageState(stored);
                }
                // Mark as resolved once we have a valid language
                languageResolvedRef.current = true;
                setIsReady(true);
                return true;
            }
            return false;
        };

        // Check immediately
        if (syncLanguage()) return;

        // Also check after AuthGatekeeper might have loaded profile
        const timers = [
            setTimeout(syncLanguage, 100),
            setTimeout(syncLanguage, 300),
            setTimeout(syncLanguage, 600),
            setTimeout(syncLanguage, 1000),
            setTimeout(() => {
                // Final timeout - mark as ready even if no profile language found
                if (!languageResolvedRef.current) {
                    console.log('[Language] No profile language found, using default');
                    languageResolvedRef.current = true;
                    setIsReady(true);
                }
            }, 2000)
        ];

        return () => timers.forEach(t => clearTimeout(t));
    }, [language]);

    // Watch for direct localStorage changes (same window)
    useEffect(() => {
        const originalSetItem = localStorage.setItem;
        localStorage.setItem = function(key, value) {
            originalSetItem.call(this, key, value);
            if (key === STORAGE_KEY) {
                const newLang = value;
                if (SUPPORTED_LANGUAGES.find(l => l.code === newLang)) {
                    console.log(`[Language] Direct localStorage update: ${newLang}`);
                    setLanguageState(prevLang => {
                        if (prevLang !== newLang) {
                            return newLang;
                        }
                        return prevLang;
                    });
                    if (!languageResolvedRef.current) {
                        languageResolvedRef.current = true;
                        setIsReady(true);
                    }
                }
            }
        };

        return () => {
            localStorage.setItem = originalSetItem;
        };
    }, []);

    // Update RTL and document direction
    useEffect(() => {
        const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === language);
        const rtl = langConfig?.rtl || false;
        setIsRTL(rtl);
        
        // Update document direction for RTL languages
        document.documentElement.dir = rtl ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
    }, [language]);

    // Set language and persist (also sync to backend for logged-in users)
    const setLanguage = useCallback(async (langCode) => {
        if (SUPPORTED_LANGUAGES.find(l => l.code === langCode)) {
            setLanguageState(langCode);
            localStorage.setItem(STORAGE_KEY, langCode);
            
            // Try to update backend profile (for logged-in users)
            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL || "";
                await fetch(`${API_URL}/api/profile/language`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ language: langCode })
                });
                console.log(`[Language] Updated backend to: ${langCode}`);
            } catch (e) {
                // Silently fail for unauthenticated users
                console.log(`[Language] Backend update skipped (user may not be logged in)`);
            }
        }
    }, []);

    // Get translation by key (supports nested keys like "onboarding.name_question")
    const t = useCallback((key, fallback = '') => {
        const keys = key.split('.');
        let value = translations[language];
        
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Fallback to English
                value = translations['en'];
                for (const fk of keys) {
                    if (value && typeof value === 'object' && fk in value) {
                        value = value[fk];
                    } else {
                        return fallback || key;
                    }
                }
                break;
            }
        }
        
        return typeof value === 'string' ? value : (fallback || key);
    }, [language]);

    // Get current language config
    const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];

    const value = {
        language,
        setLanguage,
        t,
        isRTL,
        isReady,
        currentLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

// Hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Hook for just translations (lighter)
export const useTranslation = () => {
    const { t, language, isRTL } = useLanguage();
    return { t, language, isRTL };
};

export default LanguageContext;
